import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, isPresenter } from '../../contexts/AuthContext';
import {
  canAccessTutorialSession,
  getTutorialStartPath,
  satisfiesAccessLevel
} from '../../config/featureAccess';
import { testBtnPrimary, testBtnSecondary } from '../test/testUiClasses';
import { tutorialAccentClasses } from './tutorialSessions';
import TutorialVisual from './TutorialVisual';

function TutorialSessionModal({ t, session, onClose }) {
  const navigate = useNavigate();
  const { isAuthenticated, currentUser } = useAuth();
  if (!session) return null;

  const accent = tutorialAccentClasses[session.accent] || tutorialAccentClasses.blue;
  const ctx = { isAuthenticated, isPresenter: isPresenter(currentUser) };
  const canStart = canAccessTutorialSession(session, ctx);
  const needsLogin =
    (session.access === 'authenticated' || session.access === 'presenter') && !isAuthenticated;
  const blockedPresenter =
    session.access === 'presenter' && isAuthenticated && !satisfiesAccessLevel('presenter', ctx);

  const startTutorial = () => {
    const target = getTutorialStartPath(session);
    onClose();

    if (needsLogin) {
      navigate(`/login?returnTo=${encodeURIComponent(target)}`);
      return;
    }

    if (blockedPresenter) return;

    navigate(target);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/45 flex items-center justify-center p-4">
      <div className="bg-surface rounded-2xl shadow-2xl border border-edge max-w-2xl w-full overflow-hidden">
        <div className={`h-40 ${accent.bg} border-b ${accent.border}`}>
          <TutorialVisual variant={session.visual} accent={accent} />
        </div>
        <div className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
            <div>
              <p className={`text-xs font-semibold uppercase tracking-wide ${accent.text}`}>
                {t('help.tutorialHub.sessionPreview')}
              </p>
              <h2 className="text-2xl font-bold text-ink mt-1">{t(session.titleKey)}</h2>
            </div>
            <span className={`w-fit text-xs font-semibold border rounded-md px-2 py-1 ${accent.badge}`}>
              {t(session.durationKey)}
            </span>
          </div>

          <p className="text-ink-muted text-justify mb-2">{t(session.descriptionKey)}</p>
          {!canStart && (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3">
              {blockedPresenter
                ? t('help.tutorialHub.presenterOnly')
                : t('help.tutorialHub.loginRequired')}
            </p>
          )}
          <p className="text-sm font-medium text-ink-muted mb-3">{t('help.tutorialHub.modalGuideHeading')}</p>

          <ol className="space-y-3 mb-4">
            {session.steps.map((stepKey, index) => (
              <li key={stepKey} className="flex gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-semibold">
                  {index + 1}
                </span>
                <span className="text-sm text-ink-muted pt-1">{t(stepKey)}</span>
              </li>
            ))}
          </ol>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <button type="button" onClick={onClose} className={testBtnSecondary}>
              {t('help.tutorialHub.closePreview')}
            </button>
            <button type="button" onClick={startTutorial} className={testBtnPrimary} disabled={blockedPresenter}>
              {needsLogin ? t('help.tutorialHub.startSessionLogin') : t('help.tutorialHub.startTutorial')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TutorialSessionModal;
